import type { Transition, Variants } from 'framer-motion'

/**
 * Motion preset library.
 *
 * Components import from here; they never inline durations or easings. That is what
 * keeps 40 screens feeling like one product instead of 40 different animation styles.
 *
 * Rules this file enforces by construction:
 *   • Nothing runs longer than 600ms.
 *   • Nothing loops except a genuine pending spinner.
 *   • Motion confirms a state change. It never decorates.
 */

export const easeOutExpo = [0.16, 1, 0.3, 1] as const
export const easeInOut = [0.4, 0, 0.2, 1] as const

export const DURATION = {
  instant: 0.12,
  fast: 0.2,
  base: 0.28,
  slow: 0.4,
  counter: 0.6,
} as const

/**
 * Route transitions. Short and vertical — a page change should feel like a step,
 * not a slide show.
 *
 * `exit` is intentionally retained but UNUSED by the router. AppShell renders this
 * enter-only (no AnimatePresence): `mode="wait"` could wedge mid-exit and leave a
 * blank page. Keep the variant for modals/sheets that genuinely unmount under an
 * AnimatePresence they control.
 */
export const pageTransition: Variants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: { duration: DURATION.base, ease: easeOutExpo } },
  exit: { opacity: 0, y: -8, transition: { duration: DURATION.fast, ease: easeInOut } },
}

/** Parent of a card grid. Children arrive in sequence rather than all at once. */
export const staggerGrid: Variants = {
  initial: {},
  animate: { transition: { staggerChildren: 0.05, delayChildren: 0.08 } },
}

/**
 * Card arrival — TRANSFORM ONLY. No opacity. This is the same hard rule theme.css states for
 * the page entrance, and for the same reason.
 *
 * Chrome freezes animation timelines in a background tab: `playState` still reads "running"
 * while `currentTime` stops advancing. Anything whose 0% frame says `opacity: 0` therefore stays
 * invisible forever, content present in the DOM, nothing thrown.
 *
 * Measured on the deployed build with the tab backgrounded: sixteen animations "running",
 * currentTime pinned at 121ms, and seven of eleven navigation tiles sitting at opacity 0 with no
 * recovery but a reload. A card that cannot be seen is worse than a card that does not animate.
 */
export const cardEnter: Variants = {
  initial: { y: 16, scale: 0.985 },
  animate: { y: 0, scale: 1, transition: { duration: DURATION.slow, ease: easeOutExpo } },
}

/*
 * `fadeIn` used to live here and has been REMOVED. Its whole first frame was `opacity: 0`, which
 * makes it unusable for mounting persistent content — the freeze described above turns it
 * permanent. For a genuinely user-triggered fade inside an AnimatePresence, use `modalBackdrop`,
 * which is scoped to something the reader just opened with the tab in front of them.
 */

export const slideUp: Variants = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: DURATION.base, ease: easeOutExpo } },
  exit: { opacity: 0, y: 12, transition: { duration: DURATION.fast } },
}

/** Modal: scale up from just under full size, never from zero. */
export const modalContent: Variants = {
  initial: { opacity: 0, scale: 0.96, y: 8 },
  animate: { opacity: 1, scale: 1, y: 0, transition: { duration: DURATION.fast, ease: easeOutExpo } },
  exit: { opacity: 0, scale: 0.97, y: 4, transition: { duration: DURATION.instant } },
}

export const modalBackdrop: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: DURATION.fast } },
  exit: { opacity: 0, transition: { duration: DURATION.instant } },
}

/** Bottom sheet on mobile. */
export const sheet: Variants = {
  initial: { y: '100%' },
  animate: { y: 0, transition: { type: 'spring', stiffness: 320, damping: 34 } },
  exit: { y: '100%', transition: { duration: DURATION.fast, ease: easeInOut } },
}

/** Gauge needles and band markers — spring, because a physical dial has inertia. */
export const gaugeSpring: Transition = { type: 'spring', stiffness: 120, damping: 18, mass: 0.8 }

/** Progress fills driven by real elapsed time, not a decorative animation. */
export const progressFill: Transition = { duration: DURATION.slow, ease: easeOutExpo }

/** One-shot success check draw. Explicitly not looped. */
export const checkDraw: Variants = {
  initial: { pathLength: 0, opacity: 0 },
  animate: {
    pathLength: 1,
    opacity: 1,
    transition: { pathLength: { duration: 0.5, ease: easeOutExpo }, opacity: { duration: 0.1 } },
  },
}

export const toastEnter: Variants = {
  initial: { opacity: 0, y: 16, scale: 0.96 },
  animate: { opacity: 1, y: 0, scale: 1, transition: { duration: DURATION.fast, ease: easeOutExpo } },
  exit: { opacity: 0, x: 24, transition: { duration: DURATION.fast } },
}

/** Interactive affordances. Kept small — big hover scales read as a toy. */
export const pressable = {
  whileHover: { y: -1 },
  whileTap: { scale: 0.985 },
  transition: { duration: DURATION.instant },
} as const
